import { Component } from '@verndale/core';
import {
  AnimationControls,
  ElementOrSelector,
  stagger,
  timeline
} from 'motion';
import { TimelineDefinition } from '@motionone/dom/types/timeline/types';
import UtilityNavigation from '../UtilityNavigation';
import HeaderSearch from '../HeaderSearch';
import { EventBusSingleton } from '../../../animation/eventBus';
import { breakpoints } from '../../../helpers/variables';

class MobileGlobalNavigation extends Component {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any;
  showMenuTimeline: AnimationControls | null = null;
  backTimeline: AnimationControls | null = null;
  activeMenu: HTMLElement | null = null;
  currentLevel = 1;
  level1Height = 0;
  level2Height = 0;
  level3Height = 0;
  level1Items = [] as HTMLElement[];
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  breakpoint = window.matchMedia(
    `(min-width: ${breakpoints.tabletLandscape}px)`
  );
  hasMastheadPlayed: string | null;

  constructor(el: HTMLElement) {
    super(el);
    this.hasMastheadPlayed = localStorage.getItem(
      'manhattan_masthead-animation'
    );
    this.level1Items = [];
    if (this.dom.headerSearch)
      this.level1Items.push(this.dom.headerSearch as HTMLElement);
    if (this.dom.level1Links)
      this.level1Items.push(
        ...(this.dom.level1Links as NodeListOf<HTMLElement>)
      );
    if (this.dom.cta) this.level1Items.push(this.dom.cta as HTMLElement);
    if (this.dom.utilityNavigation) {
      this.level1Items.push(this.dom.utilityNavigation as HTMLElement);
    }

    if (this.hasMastheadPlayed === null) {
      if (!this.reducedMotion.matches) {
        if (!document.querySelector('.intro-animation-overlay')) {
          this.initialDelay = 0;
          this.animateIn();
        } else {
          EventBusSingleton.subscribe(
            'introAnimationOverlay:hide',
            this.animateIn.bind(this)
          );
        }
      }
    }
  }

  setupDefaults() {
    this.dom = {
      logo: this.el.querySelector<HTMLElement>('.global-navigation__logo'),
      navigationItemsWrapper: this.el.querySelector<HTMLElement>(
        '.global-navigation__items'
      ),
      mobileMenuTrigger: this.el.querySelector<HTMLElement>(
        '.global-navigation__mobile-menu-trigger'
      ),
      container: this.el.querySelector<HTMLElement>(
        '.global-navigation__container'
      ),
      navigationItems: this.el.querySelectorAll<HTMLElement>(
        '.global-navigation__item'
      ),
      globalNavigationWrapper: this.el.querySelector<HTMLElement>(
        '.global-navigation__items-wrapper'
      ),
      utilityNavigation: this.el.querySelector<HTMLElement>(
        '.utility-navigation'
      ),
      headerSearch: this.el.querySelector<HTMLElement>('.header-search'),
      cta: this.el.querySelector<HTMLElement>('.global-navigation__cta'),
      level2MenuTriggers: this.el.querySelectorAll<HTMLElement>(
        '.global-navigation__menu-trigger'
      ),
      level3MenuTriggers: this.el.querySelectorAll<HTMLElement>(
        '.global-navigation__menu-level-3-trigger'
      ),
      level1Links: this.el.querySelectorAll<HTMLElement>(
        '.global-navigation__item > .link'
      ),
      backButtons: this.el.querySelectorAll<HTMLElement>(
        '.global-navigation__back-button'
      ),
      background: this.el.querySelector<HTMLElement>(
        '.global-navigation__background'
      )
    };

    this.utilityNavigation = new UtilityNavigation(
      this.dom.utilityNavigation as HTMLElement
    );
    this.headerSearch = new HeaderSearch(this.dom.headerSearch as HTMLElement);

    this.listenerRefs = {
      toggleMobileMenu: this.toggleMobileMenu.bind(this),
      toggleLevel2Menu: this.toggleLevel2Menu.bind(this),
      toggleLevel3Menu: this.toggleLevel3Menu.bind(this),
      handleBackButton: this.handleBackButton.bind(this),
      handleClickOutside: this.handleClickOutside.bind(this)
    };
  }

  addListeners() {
    (this.dom.mobileMenuTrigger as HTMLElement).addEventListener(
      'click',
      this.listenerRefs.toggleMobileMenu
    );
    [].forEach.call(this.dom.level2MenuTriggers, (trigger: HTMLElement) => {
      trigger.addEventListener('click', this.listenerRefs.toggleLevel2Menu);
    });
    [].forEach.call(this.dom.level3MenuTriggers, (trigger: HTMLElement) => {
      trigger.addEventListener('click', this.listenerRefs.toggleLevel3Menu);
    });
    [].forEach.call(this.dom.backButtons, (backButton: HTMLElement) => {
      backButton.addEventListener('click', this.listenerRefs.handleBackButton);
    });

    document.addEventListener(
      'click',
      this.listenerRefs.handleClickOutside,
      true
    );

    EventBusSingleton.subscribe(
      'utilityNavigation:openingLanguageSelector',
      this.handleLanguageSelectorOpen.bind(this)
    );

    EventBusSingleton.subscribe(
      'utilityNavigation:closingLanguageSelector',
      () => {
        if (this.breakpoint.matches) return;
        this.level1Items.forEach((item: HTMLElement) => {
          item.style.display = '';
          if (item.classList.contains('utility-navigation')) {
            item
              .querySelectorAll<HTMLElement>(
                '.utility-navigation__section > *:not(language-selector)'
              )
              .forEach((el: HTMLElement) => {
                el.style.display = '';
              });
          }
          (this.dom.utilityNavigation as HTMLElement).classList.remove(
            'language-selector--open'
          );
        });
      }
    );

    EventBusSingleton.subscribe('utilityNavigation:openingHeaderSearch', () => {
      if (this.breakpoint.matches) return;
      this.level1Items.forEach((item: HTMLElement) => {
        if (!item.classList.contains('header-search'))
          item.style.display = 'none';
      });
    });

    EventBusSingleton.subscribe('utilityNavigation:closingHeaderSearch', () => {
      if (this.breakpoint.matches) return;
      this.resetMenu();
      this.openMobileMenu(this.dom.mobileMenuTrigger as HTMLElement);
    });
  }

  animateIn() {
    (this.dom.container as HTMLElement).style.overflow = 'hidden';

    const staggerItems = [] as HTMLElement[];
    staggerItems.push(this.dom.logo as HTMLElement);
    staggerItems.push(this.dom.mobileMenuTrigger as HTMLElement);

    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push([
      this.dom.background as ElementOrSelector,
      {
        x: [`${window.innerWidth}px`, 0]
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 1.25,
        at: 'start'
      }
    ]);
    sequence.push([
      this.dom.background as ElementOrSelector,
      {
        scaleX: [0, 1]
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 1.2,
        at: 'start',
        easing: 'linear'
      }
    ]);
    sequence.push([
      staggerItems,
      {
        opacity: [0, 1],
        transform: ['translateY(10px)', 'translateY(0)']
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5,
        at: 'stagger-items',
        delay: this.reducedMotion.matches
          ? this.MOTION_ZERO_DURATION
          : stagger(0.5),
        opacity: {
          duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 1
        }
      }
    ]);
    timeline(sequence, { delay: this.initialDelay }).finished.then(() => {
      (this.dom.container as HTMLElement).style.overflow = '';
      staggerItems.forEach(item => {
        item.style.opacity = '';
        item.style.transform = '';
      });
    });
  }

  handleLanguageSelectorOpen() {
    if (this.breakpoint.matches) return;
    const staggerItems = this.level1Items.filter(
      (item: HTMLElement) => !item.classList.contains('utility-navigation')
    );
    const navWrapper = this.dom.globalNavigationWrapper as HTMLElement;
    if (!navWrapper) return;
    navWrapper.style.overflow = 'hidden';
    staggerItems.forEach((item: HTMLElement) => {
      item.style.display = 'none';
    });
    (this.dom.utilityNavigation as HTMLElement)
      .querySelectorAll<HTMLElement>(
        '.utility-navigation__section > *:not(language-selector)'
      )
      .forEach((el: HTMLElement) => {
        el.style.display = 'none';
      });
  }

  handleClickOutside(e: MouseEvent) {
    if (this.breakpoint.matches) return;
    if (!this.el.contains(e.target as Node)) {
      this.closeMobileMenu(this.dom.mobileMenuTrigger as HTMLElement);
    }
  }

  removeListeners() {
    (this.dom.mobileMenuTrigger as HTMLElement).removeEventListener(
      'click',
      this.listenerRefs.toggleMobileMenu
    );
    [].forEach.call(this.dom.level2MenuTriggers, (trigger: HTMLElement) => {
      trigger.removeEventListener('click', this.listenerRefs.toggleLevel2Menu);
    });
    [].forEach.call(this.dom.level3MenuTriggers, (trigger: HTMLElement) => {
      trigger.removeEventListener('click', this.listenerRefs.toggleLevel3Menu);
    });
    [].forEach.call(this.dom.backButtons, (backButton: HTMLElement) => {
      backButton.removeEventListener(
        'click',
        this.listenerRefs.handleBackButton
      );
    });
    document.removeEventListener(
      'click',
      this.listenerRefs.handleClickOutside,
      true
    );
  }

  async handleBackButton() {
    if (this.currentLevel === 1) return;
    if (this.currentLevel === 2) this.hideLevel2Menu();
    if (this.currentLevel === 3) this.hideLevel3Menu();
  }

  toggleMobileMenu(e: Event) {
    const trigger = e.currentTarget as HTMLElement;
    if (trigger.getAttribute('aria-expanded') === 'false') {
      this.openMobileMenu(trigger);
    } else {
      this.closeMobileMenu(trigger);
    }
  }

  openMobileMenu(trigger: HTMLElement) {
    const menuTarget = trigger.getAttribute('aria-controls');
    if (!menuTarget) return;
    const menu = document.getElementById(menuTarget) as HTMLElement;
    menu.style.display = 'block';
    menu.style.visibility = 'visible';
    menu.style.overflow = 'hidden';
    menu.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    menu.style.overflow = '';
    menu.style.blockSize = '';
  }

  closeMobileMenu(trigger: HTMLElement) {
    const menuTarget = trigger.getAttribute('aria-controls');
    if (!menuTarget) return;
    const menu = document.getElementById(menuTarget) as HTMLElement;
    if (menu) {
      menu.style.overflow = 'hidden';
      menu.style.overflow = '';
      menu.style.blockSize = '';
      menu.style.display = 'none';
      menu.style.visibility = 'hidden';
      menu.setAttribute('aria-hidden', 'true');
    }
    trigger.setAttribute('aria-expanded', 'false');
    (this.dom.headerSearch as HTMLElement)?.classList.remove(
      'header-search--open'
    );
    (this.dom.headerSearch as HTMLElement)?.classList.remove(
      'featured-topics--open'
    );
    (this.dom.headerSearch as HTMLElement)
      ?.querySelector('.header-search__form')
      ?.classList.remove('header-search__input--error');

    this.resetMenu();
  }

  async toggleLevel2Menu(e: Event) {
    const trigger = e.currentTarget as HTMLElement;
    const menuTarget = trigger.getAttribute('aria-controls');
    if (!menuTarget) return;
    const menu = document.getElementById(menuTarget) as HTMLElement;
    if (trigger.getAttribute('aria-expanded') === 'false')
      this.showLevel2Menu(menu, trigger);
  }

  showLevel2Menu(menu: HTMLElement, trigger: HTMLElement) {
    const navWrapper = this.dom.globalNavigationWrapper as HTMLElement;
    if (!navWrapper) return;
    navWrapper.style.overflow = 'hidden';
    menu.style.display = 'block';
    menu.style.visibility = 'visible';
    menu.setAttribute('aria-hidden', 'false');
    this.level1Items.forEach((item: HTMLElement) => {
      item.style.display = 'none';
    });
    this.level2Height = menu.scrollHeight;

    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push([
      navWrapper,
      { blockSize: [this.level1Height, `${this.level2Height}px`] },
      { duration: 0.25, easing: 'linear', at: 'start' }
    ]);
    sequence.push([
      menu,
      {
        opacity: [0, 1],
        transform: ['translate(100%, 0)', 'translate(0, 0)']
      },
      { duration: 0.25, easing: 'linear', at: 'start' }
    ]);
    timeline(sequence).finished.then(() => {
      this.currentLevel = 2;
      this.activeMenu = menu;
      trigger.setAttribute('aria-expanded', 'true');
      navWrapper.style.overflow = '';
      navWrapper.style.blockSize = '';
    });
  }

  async hideLevel2Menu() {
    const navWrapper = this.dom.globalNavigationWrapper as HTMLElement;
    navWrapper.style.overflow = 'hidden';
    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push([
      this.activeMenu as HTMLElement,
      {
        opacity: [1, 0],
        transform: ['translate(0, 0)', 'translate(100%, 0)']
      },
      { duration: 0.25, easing: 'linear', at: 'start' }
    ]);
    sequence.push([
      navWrapper,
      {
        blockSize: [`${this.level2Height}px`, `${this.level1Height}px`]
      },
      { duration: 0.25, easing: 'linear', at: 'start' }
    ]);
    await timeline(sequence).finished.then(() => {
      (this.activeMenu as HTMLElement).style.display = 'none';
      this.resetLevel1Menu();
    });
    const sequence2 = [] as TimelineDefinition;
    sequence2.push({ name: 'start', at: 0 });
    sequence2.push([
      this.level1Items,

      { opacity: [0, 1] },
      { duration: 0.15, easing: 'linear', at: 'start' }
    ]);
    await timeline(sequence2).finished.then(() => {
      this.currentLevel = 1;
      navWrapper.style.overflow = '';
      navWrapper.style.blockSize = '';
    });
  }

  async toggleLevel3Menu(e: Event) {
    const trigger = e.currentTarget as HTMLElement;
    const menuTarget = trigger.getAttribute('aria-controls');
    if (!menuTarget) return;
    const menu = document.getElementById(menuTarget) as HTMLElement;
    if (trigger.getAttribute('aria-expanded') === 'false')
      this.showLevel3Menu(menu, trigger);
  }

  showLevel3Menu(menu: HTMLElement, trigger: HTMLElement) {
    const navWrapper = this.dom.globalNavigationWrapper as HTMLElement;
    if (!navWrapper) return;
    navWrapper.style.overflow = 'hidden';

    const correspondingLevel2Menu = (
      this.activeMenu as HTMLElement
    ).querySelector('.global-navigation__menu-left');
    menu.style.display = 'block';
    menu.style.visibility = 'visible';
    menu.setAttribute('aria-hidden', 'false');
    this.level3Height = menu.scrollHeight;

    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push([
      navWrapper,
      { blockSize: [this.level2Height, `${this.level3Height}px`] },
      { duration: 0.25, easing: 'linear', at: 'start' }
    ]);
    sequence.push([
      this.activeMenu as ElementOrSelector,
      {
        transform: ['translate(0, 0)', 'translate(-50%, 0)']
      },
      { duration: 0.25, easing: 'linear', at: 'start' }
    ]);
    timeline(sequence).finished.then(() => {
      (correspondingLevel2Menu as HTMLElement).style.blockSize = '0px';
      this.currentLevel = 3;
      trigger.setAttribute('aria-expanded', 'true');
      (this.dom.globalNavigationWrapper as HTMLElement).style.overflow = '';
      (this.dom.globalNavigationWrapper as HTMLElement).style.blockSize = '';
    });
  }

  async hideLevel3Menu() {
    const navWrapper = this.dom.globalNavigationWrapper as HTMLElement;
    navWrapper.style.overflow = 'hidden';
    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push([
      this.activeMenu as HTMLElement,
      {
        transform: 'translate(0, 0)'
      },
      { duration: 0.25, easing: 'linear', at: 'start' }
    ]);
    sequence.push([
      this.activeMenu?.querySelector(
        '.global-navigation__menu-left'
      ) as HTMLElement,
      {
        opacity: [0, 1],
        blockSize: `${this.level2Height}px`
      },
      {
        duration: 0.15,
        easing: 'linear',
        at: 'start',
        blockSize: { duration: 0.1 }
      }
    ]);
    sequence.push([
      navWrapper,
      {
        blockSize: [`${this.level3Height}px`, `${this.level2Height}px`]
      },
      { duration: 0.25, easing: 'linear', at: 'start' }
    ]);
    await timeline(sequence).finished.then(() => {
      const menu3Wrappers = this.activeMenu?.querySelectorAll(
        '.global-navigation__level-3-wrapper'
      ) as NodeListOf<HTMLElement>;
      menu3Wrappers.forEach((t: HTMLElement) => {
        t.style.display = 'none';
        t.style.visibility = 'hidden';
        t.setAttribute('aria-hidden', 'true');
      });

      this.currentLevel = 2;
      this.resetLevel2Menu();
    });
  }

  resetMenu() {
    const items = (this.dom.utilityNavigation as HTMLElement)?.querySelectorAll(
      ':scope *'
    ) as NodeListOf<HTMLElement>;
    if (!items) return;
    items.forEach((item: HTMLElement) => {
      if (item.getAttribute('aria-expanded') === 'true')
        item.setAttribute('aria-expanded', 'false');
      item.removeAttribute('style');
    });
    const navWrapper = this.dom.globalNavigationWrapper as HTMLElement;
    const itemsWrapper = this.dom.navigationItemsWrapper as HTMLElement;
    if (!navWrapper || !itemsWrapper) return;
    navWrapper.removeAttribute('style');
    itemsWrapper.removeAttribute('style');
    this.activeMenu?.removeAttribute('style');
    this.resetLevel1Menu();
    this.resetLevel2Menu();
    (this.dom.headerSearch as HTMLElement)
      .querySelector('.header-search__featured-section')
      ?.removeAttribute('style');

    (this.dom.utilityNavigation as HTMLElement)
      .querySelector('.language-selector')
      ?.removeAttribute('style');
    (this.dom.utilityNavigation as HTMLElement)
      .querySelector('.language-selector')
      ?.classList.remove('language-selector--open');
    (this.dom.utilityNavigation as HTMLElement).classList.remove(
      'language-selector--open'
    );
  }

  resetLevel1Menu() {
    [].forEach.call(this.dom.level1Links, (link: HTMLElement) => {
      (link as HTMLElement).setAttribute('aria-expanded', 'false');
    });
    this.level1Items.forEach((item: HTMLElement) => {
      item.removeAttribute('style');
    });
  }

  resetLevel2Menu() {
    [].forEach.call(
      (
        this.dom.navigationItemsWrapper as HTMLElement
      ).querySelectorAll<HTMLEmbedElement>('.global-navigation__menu-left'),
      (menu: HTMLElement) => {
        menu.removeAttribute('style');
        [].forEach.call(menu.querySelectorAll('.link'), (link: HTMLElement) => {
          link.removeAttribute('style');
          if (link.getAttribute('aria-expanded'))
            link.setAttribute('aria-expanded', 'false');
        });
      }
    );
  }
}

export default MobileGlobalNavigation;
